import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, apiBaseUrl, useApi } from '@/api/client';
import { fileToAttachment } from '@/lib/attachments';
import { buildSnippet, snippetLanguages, type SnippetLanguage } from '@/lib/copyAsCode';
import { MarkdownAnswer } from '@/components/MarkdownAnswer';
import { isDecisionsModel } from '@/components/DecisionsBadge';
import { DecisionsPlayground } from '@/pages/DecisionsPlayground';
import { fmtNumber, fmtUsd } from '@/lib/format';
import type { AiEffort, Attachment, AvailableMcpServer, ChatMessage, CompletionRequest, CompletionResponse, CompletionUsage, ModelInfo, PiiMode, PromptTemplateDto, StreamToolCall, ToolCallSummary } from '@/types';

/** One exchange in the transcript. Assistant turns carry the usage the call reported. */
interface Turn {
  role: 'User' | 'Assistant';
  content: string;
  attachments?: Attachment[];
  usage?: CompletionUsage;
  finishReason?: string;
  correlationId?: string;
  toolCalls?: ToolCallSummary[] | null;
  toolBudgetExhausted?: boolean;
  servedFromGatewayCache?: boolean;
  piiRedactionApplied?: boolean;
  /** Stamped per turn: switching model mid-thread is normal here, and the transcript should say
   *  which one actually answered rather than implying the currently-selected one did. */
  supplier?: string;
  modelId?: string;
}

/**
 * Tokens this turn occupied in the window: everything the model read, plus what it wrote —
 * which is what the next turn carries forward as input.
 *
 * The two supplier families count caching differently, and getting this wrong is a large error,
 * not a rounding one: Anthropic reports input/cache-read/cache-write as DISJOINT buckets, while
 * the OpenAI-compatible suppliers fold the cached portion into inputTokens. Adding cache counts
 * for the latter would double-count every cached prefix.
 */
function contextTokensUsed(supplier: string, usage: CompletionUsage): number {
  const promptTokens = supplier === 'Anthropic'
    ? usage.inputTokens + usage.cacheReadInputTokens + usage.cacheCreationInputTokens
    : usage.inputTokens;
  return promptTokens + usage.outputTokens;
}


/** A model that bills at two rates depending on prompt size (OpenAI's 5.4/5.5/5.6 families). */
function isTiered(m: ModelInfo | undefined): boolean {
  return !!m?.longContextThresholdTokens && m.longContextInputCostPerMillionUsd != null;
}

/**
 * Which tier a prompt of this size bills at. The boundary is exclusive — the supplier switches
 * ABOVE the threshold, so a prompt landing exactly on it is still the cheap tier.
 */
function isLongContext(m: ModelInfo | undefined, promptTokens: number): boolean {
  return isTiered(m) && promptTokens > (m!.longContextThresholdTokens as number);
}


/**
 * Prompt tokens as the supplier counts them for tier selection. Mirrors CostCalculator.PromptTokens
 * on the server: Anthropic's buckets are disjoint and add, the OpenAI-compatible ones already fold
 * cached tokens into inputTokens.
 */
function promptTokensOf(supplier: string, usage: CompletionUsage): number {
  return supplier === 'Anthropic'
    ? usage.inputTokens + usage.cacheReadInputTokens + usage.cacheCreationInputTokens
    : usage.inputTokens;
}

const friendlyByCode: Record<string, string> = {
  caller_not_registered: 'You are not registered for the AI gateway yet. Request access from the Home page first.',
  caller_not_active: 'Your registration is not active yet. If you have just requested access, an administrator still needs to approve it.',
  model_not_allowed: 'That model is not enabled on the gateway. Pick a different model from the list.',
  budget_exhausted: 'Your monthly budget is used up. Ask an administrator to raise it, or wait until next month.',
  supplier_error: 'The AI supplier returned an error. This is usually transient — try again in a moment.',
  supplier_key_missing: 'No licence key is configured for this supplier yet. An administrator needs to add one under Admin > Suppliers.',
  pii_redaction_unavailable: 'PII redaction is currently unavailable. Retry shortly, or switch PII mode to None if your prompt contains no personal data.',
  attachment_not_supported: 'This model cannot accept that attachment type — check the capability icons under the model picker.',
  attachment_invalid: 'An attachment was rejected — check the file type (JPEG/PNG/GIF/WebP images or PDF) and size (10 MB per file, 25 MB total).',
  pii_attachments_unsupported: 'PII redaction only covers text, so attachments need the "send unscanned" acknowledgement below.'
};

/** A run the user stopped, rather than one that failed — not worth an error banner. */
function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError';
}

function friendlyError(e: unknown): { message: string; code?: string } {
  if (e instanceof ApiError) {
    const mapped = e.code ? friendlyByCode[e.code] : undefined;
    return { message: mapped ?? e.detail ?? e.message, code: e.code };
  }
  return { message: e instanceof Error ? e.message : 'Something went wrong.' };
}

const piiModes: { value: PiiMode; label: string }[] = [
  { value: 'None', label: 'None — send prompt as-is' },
  { value: 'RedactAndRestore', label: 'Redact and restore — mask PII, restore in the reply' },
  { value: 'Redact', label: 'Redact — mask PII permanently' }
];

type PlaygroundMode = 'chat' | 'decisions';

/**
 * Chat | Decisions. Both modes stay mounted and the inactive one is hidden, so flipping across to
 * try a Decisions model never throws away a conversation in progress. The mode (and a model to
 * preselect) can come from the URL, which is how Ctrl+K and the "Open the Decisions tab" note land
 * in the right place.
 */
export function Playground() {
  const [params, setParams] = useSearchParams();
  const mode: PlaygroundMode = params.get('mode') === 'decisions' ? 'decisions' : 'chat';
  const linkedModel = params.get('model') ?? '';

  const setMode = (next: PlaygroundMode) => {
    const p = new URLSearchParams(params);
    if (next === 'chat') p.delete('mode'); else p.set('mode', next);
    p.delete('model');
    setParams(p, { replace: true });
  };

  const modes: PlaygroundMode[] = ['chat', 'decisions'];
  const onModeKey = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = modes[(modes.indexOf(mode) + 1) % modes.length];
    setMode(next);
    document.getElementById(`pg-mode-${next}`)?.focus();
  };

  return (
    <>
      <div className="pg-head">
        <h1>Playground</h1>
        <div className="mode-tabs" role="tablist" aria-label="Mode">
          {modes.map(m => (
            <button key={m} id={`pg-mode-${m}`} type="button" role="tab" className="mode-tab"
              aria-selected={mode === m} aria-controls={`pg-panel-${m}`} tabIndex={mode === m ? 0 : -1}
              onClick={() => setMode(m)} onKeyDown={onModeKey}>
              {m === 'chat' ? 'Chat' : 'Decisions'}
            </button>
          ))}
        </div>
      </div>
      <div id="pg-panel-chat" role="tabpanel" aria-labelledby="pg-mode-chat" hidden={mode !== 'chat'}>
        <ChatPlayground
          initialModelKey={mode === 'chat' ? linkedModel : ''}
          onOpenDecisions={() => setMode('decisions')} />
      </div>
      <div id="pg-panel-decisions" role="tabpanel" aria-labelledby="pg-mode-decisions" hidden={mode !== 'decisions'}>
        <DecisionsPlayground initialModelId={mode === 'decisions' ? linkedModel : ''} />
      </div>
    </>
  );
}

function ChatPlayground({ initialModelKey, onOpenDecisions }: { initialModelKey: string; onOpenDecisions: () => void }) {
  const api = useApi();
  // Chat only: Decisions models return typed choices, not text, and completions refuse them, so
  // they're filtered out here once rather than at every picker. Same cache entry, two views.
  const models = useQuery({ queryKey: ['models'], queryFn: api.listModels, select: ms => ms.filter(m => !isDecisionsModel(m)) });
  const hiddenDecisionsModels = useQuery({ queryKey: ['models'], queryFn: api.listModels, select: ms => ms.filter(isDecisionsModel).length });
  const templates = useQuery({ queryKey: ['prompt-templates'], queryFn: api.listPromptTemplates });

  const [modelKey, setModelKey] = useState(initialModelKey);
  // A model linked from Ctrl+K while the page is already open.
  useEffect(() => { if (initialModelKey) setModelKey(initialModelKey); }, [initialModelKey]);
  const [templateName, setTemplateName] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [message, setMessage] = useState('');
  const [maxTokens, setMaxTokens] = useState(10000);
  const [piiMode, setPiiMode] = useState<PiiMode>('None');
  const [effort, setEffort] = useState<AiEffort | ''>('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [acknowledgeUnscanned, setAcknowledgeUnscanned] = useState(false);
  const [selectedServers, setSelectedServers] = useState<Set<string>>(new Set());
  const [serverCredentials, setServerCredentials] = useState<Record<string, string>>({});
  const [maxToolCalls, setMaxToolCalls] = useState(20);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const mcpServers = useQuery({ queryKey: ['mcp-servers'], queryFn: api.listMcpServers });
  // Already fetched by App; this reads the same cache entry rather than re-requesting.
  const me = useQuery({ queryKey: ['me'], queryFn: api.me });
  // Admin-set ceiling on tools.maxToolCalls. The server rejects anything above it, so the
  // input is bounded here too rather than letting someone type a value that will 403.
  const toolCallCeiling = me.data?.maxToolCallsLimit ?? 30;
  // Rail is always visible above 900px; below it this drives the sheet.
  const [railOpen, setRailOpen] = useState(false);

  /**
   * Stick the transcript to the bottom as tokens stream in — but only while the reader is
   * already there.
   *
   * Scrolling unconditionally would drag someone back down the moment they scrolled up to
   * re-read an earlier turn, which is worse than not following at all: a long answer would make
   * the thread unreadable while it streams. So a scroll away from the bottom releases the pin,
   * and returning to the bottom takes it again — the behaviour every chat client has trained
   * people to expect.
   */
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pinnedRef = useRef(true);

  /**
   * Which element actually scrolls. Above 900px it is the transcript panel; below it the rail
   * becomes a sheet, .wb-scroll drops its max-height and the PAGE scrolls instead — so following
   * the container there would silently do nothing on a phone.
   */
  const activeScroller = (): HTMLElement | null => {
    const el = scrollRef.current;
    if (el && el.scrollHeight > el.clientHeight + 1) return el;
    return document.scrollingElement as HTMLElement | null;
  };

  // 60px of slack: exact equality never holds with sub-pixel heights or elastic overscroll.
  const onTranscriptScroll = () => {
    const el = activeScroller();
    if (!el) return;
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  // The page-scrolling case needs a window listener; the container's own onScroll never fires.
  useEffect(() => {
    window.addEventListener('scroll', onTranscriptScroll, { passive: true });
    return () => window.removeEventListener('scroll', onTranscriptScroll);
  }, []);

  /**
   * Stay at the bottom by watching the DOM, not React state.
   *
   * A state-keyed effect fires when the data changes, which is not the same moment the content
   * finishes laying out — markdown blocks mounting, a code fence closing, text rewrapping. Scroll
   * at the wrong moment and scrollHeight is still the old (short) value, so "scroll to the bottom"
   * lands part-way up. Observing mutations and resizes instead means every height change re-pins,
   * whenever it actually happens, which is what makes the thread sit at the bottom and push up as
   * a reply grows.
   */
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const stick = () => {
      if (!pinnedRef.current) return;
      const el = activeScroller();
      if (el) el.scrollTop = el.scrollHeight;
    };

    // characterData catches streamed text landing in an existing node; childList/subtree catch
    // new turns and markdown re-renders.
    const mutations = new MutationObserver(stick);
    mutations.observe(container, { childList: true, subtree: true, characterData: true });

    // Height changes that mutate nothing — a late font, an image, the viewport resizing.
    const resizes = new ResizeObserver(stick);
    resizes.observe(container);

    return () => { mutations.disconnect(); resizes.disconnect(); };
  }, []);

  const addFiles = async (files: FileList | null) => {
    if (!files) return;
    setAttachError(null);
    try {
      const added = await Promise.all([...files].map(fileToAttachment));
      setAttachments(prev => [...prev, ...added]);
    } catch (e) {
      setAttachError(e instanceof Error ? e.message : 'Could not read that file.');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const bySupplier = useMemo(() => {
    const groups = new Map<string, ModelInfo[]>();
    for (const m of models.data ?? []) {
      const list = groups.get(m.supplier) ?? [];
      list.push(m);
      groups.set(m.supplier, list);
    }
    return [...groups.entries()];
  }, [models.data]);

  // One option per template name (latest version wins if the API ever returns several).
  const templateOptions = useMemo(() => {
    const byName = new Map<string, PromptTemplateDto>();
    for (const t of templates.data ?? []) {
      const existing = byName.get(t.name);
      if (!existing || t.version > existing.version) byName.set(t.name, t);
    }
    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [templates.data]);

  const selected = (models.data ?? []).find(m => `${m.supplier}::${m.modelId}` === modelKey);
  const selectedTemplate = templateOptions.find(t => t.name === templateName);

  // Live streaming state: text accumulates as it arrives; tool calls flip started -> completed.
  const [streamText, setStreamText] = useState('');
  const [liveTools, setLiveTools] = useState<StreamToolCall[]>([]);

  // Multi-turn state. keepContext off reproduces the original one-off behaviour: only the
  // current message is sent and nothing accumulates.
  const [keepContext, setKeepContext] = useState(true);
  const [turns, setTurns] = useState<Turn[]>([]);
  // Output from a run the user stopped. Shown so the partial answer is not simply thrown away,
  // but held outside `turns` — a half-finished reply must never become context for a later turn.
  const [cancelled, setCancelled] = useState<string | null>(null);
  // The message currently in flight. The composer is emptied the moment you send, the way every
  // chat interface behaves, so this holds what was sent: the pending turn renders from it, the
  // completed turn is built from it, and a failure puts it BACK in the box so nothing typed is
  // lost to an error.
  const [inFlight, setInFlight] = useState<{ content: string; attachments: Attachment[] } | null>(null);
  // Aborts the in-flight request. Held in a ref so Cancel reaches the live controller rather
  // than one captured by an earlier render.
  const abortRef = useRef<AbortController | null>(null);

  // Selected servers that run as the caller. A typed key is sent on the request and overrides
  // whatever is saved, for this message only; a server with a saved key needs nothing typed.
  const credentialServers = (mcpServers.data ?? []).filter(s => s.requiresUserCredential && selectedServers.has(s.name));
  const suppliedCredentials = Object.fromEntries(
    credentialServers.map(s => [s.name, (serverCredentials[s.name] ?? '').trim()]).filter(([, v]) => v.length > 0)
  );
  const missingCredentials = credentialServers.filter(
    s => !s.hasSavedCredential && !(serverCredentials[s.name] ?? '').trim()
  );

  /**
   * The transcript replayed as wire messages. Assistant turns go back as their final text only:
   * the gateway runs the tool loop server-side and returns the answer, so intermediate tool
   * traffic is not ours to replay — the model sees what it said, not how it got there.
   */
  const historyMessages = (): ChatMessage[] => turns.map(t => ({
    role: t.role,
    content: t.content,
    attachments: t.role === 'User' && t.attachments?.length ? t.attachments : undefined
  }));

  /** The wire request for the current form state — used by Run and by "copy as code". */
  const buildRequest = (): CompletionRequest => {
    if (!selected) throw new Error('Pick a model first.');
    const nextTurn: ChatMessage = {
      role: 'User',
      content: message,
      attachments: attachments.length ? attachments : undefined
    };
    return {
      supplier: selected.supplier,
      modelId: selected.modelId,
      promptTemplateName: templateName || undefined,
      systemPrompt: systemPrompt.trim() || undefined,
      messages: keepContext ? [...historyMessages(), nextTurn] : [nextTurn],
      maxTokens,
      effort: effort || undefined,
      piiMode,
      allowUnscannedAttachments: attachments.length > 0 && piiMode !== 'None' ? acknowledgeUnscanned : undefined,
      tools: selectedServers.size > 0
        ? {
            mcpServers: Array.from(selectedServers),
            maxToolCalls,
            credentials: Object.keys(suppliedCredentials).length > 0 ? suppliedCredentials : undefined
          }
        : undefined
    };
  };

  const run = useMutation<CompletionResponse, unknown>({
    mutationFn: () => {
      // Built from current state BEFORE the composer is cleared below.
      const request = buildRequest();

      setInFlight({ content: message, attachments });
      setMessage('');
      setAttachments([]);

      setStreamText('');
      setLiveTools([]);
      setCancelled(null);

      const controller = new AbortController();
      abortRef.current = controller;

      // Streamed by default: long tool loops stay alive and show progress. PII modes need the
      // sync endpoint (restore can't span chunks), and those requests are short anyway.
      if (piiMode !== 'None') {
        return api.complete(request, controller.signal);
      }

      return new Promise<CompletionResponse>((resolve, reject) => {
        api.completeStream(request, {
          onDelta: text => setStreamText(prev => prev + text),
          onToolCall: call => setLiveTools(prev => {
            if (call.state === 'started') return [...prev, call];
            // Complete the most recent still-running entry for this server/tool.
            const next = [...prev];
            for (let i = next.length - 1; i >= 0; i--) {
              if (next[i].server === call.server && next[i].tool === call.tool && next[i].state === 'started') {
                next[i] = call;
                return next;
              }
            }
            return [...next, call];
          }),
          onDone: resolve,
          onError: (code, msg) => reject(new ApiError(502, msg, code, msg))
        }, controller.signal).catch(reject);
      });
    },
    // Only a completed exchange joins the transcript. A failed call leaves the message in the
    // box and the history untouched, so retrying does not replay a turn the model never saw.
    onSuccess: response => {
      // With context off each run stands alone, so it REPLACES the panel rather than adding to
      // it — appending would render a column of unrelated exchanges that reads like a thread the
      // model can see, which is exactly the impression this mode needs to avoid.
      setTurns(prev => [
        ...(keepContext ? prev : []),
        {
          role: 'User',
          content: inFlight?.content ?? message,
          attachments: inFlight?.attachments.length ? inFlight.attachments : undefined
        },
        {
          role: 'Assistant',
          content: response.content,
          usage: response.usage,
          finishReason: response.finishReason,
          correlationId: response.correlationId,
          toolCalls: response.toolCalls,
          toolBudgetExhausted: response.toolBudgetExhausted,
          servedFromGatewayCache: response.servedFromGatewayCache,
          piiRedactionApplied: response.piiRedactionApplied,
          supplier: response.supplier,
          modelId: response.modelId
        }
      ]);
      setInFlight(null);
      setStreamText('');
    },
    // Put the message back so a failure never costs you what you typed. Guarded on the box being
    // empty: if you started typing the next one while this was in flight, that wins.
    onError: () => {
      setMessage(prev => (prev.trim() === '' ? inFlight?.content ?? '' : prev));
      setAttachments(prev => (prev.length === 0 ? inFlight?.attachments ?? [] : prev));
      setInFlight(null);
    }
  });

  const newConversation = () => {
    setTurns([]);
    setStreamText('');
    setLiveTools([]);
    setCancelled(null);
    setInFlight(null);
    run.reset();
  };

  /**
   * Stops the in-flight call. Whatever the model produced before the abort is kept on screen but
   * outside the transcript, and the message stays in the box so it can be re-sent.
   *
   * Cancelling does not make the call free: the gateway bills the tokens that were actually
   * generated before the disconnect, which is why the notice says so rather than implying undo.
   */
  const cancelRun = () => {
    setCancelled(streamText);
    abortRef.current?.abort();
  };

  /**
   * Switching mode always starts a clean slate. Leaving the old transcript on screen after
   * turning context off is actively misleading — it looks like history the model can still see,
   * when nothing on screen is being sent any more.
   */
  const changeKeepContext = (on: boolean) => {
    setKeepContext(on);
    newConversation();
  };

  // Context meter: measured from the last completed turn, since only the supplier can tell us
  // what the prompt actually tokenised to. Undefined until the first answer comes back.
  const sessionCostUsd = turns.reduce((sum, t) => sum + (t.usage?.estimatedCostUsd ?? 0), 0);
  const lastAssistant = [...turns].reverse().find(t => t.role === 'Assistant');
  const contextUsed = lastAssistant?.usage && selected
    ? contextTokensUsed(selected.supplier, lastAssistant.usage)
    : undefined;
  const contextLimit = selected?.maxContextTokens ?? undefined;

  const support = { images: selected?.supportsImageInput ?? false, documents: selected?.supportsDocumentInput ?? false };
  const needsAcknowledgement = attachments.length > 0 && piiMode !== 'None';
  const toolsWithPii = selectedServers.size > 0 && piiMode !== 'None';
  // OpenAI's gpt-5.6 tier refuses tools and reasoning on the same call, so the gateway drops effort
  // for the tool-calling turns. Say so rather than let a chosen effort quietly do nothing.
  const effortDroppedForTools = !!selected && selected.supportsEffort
    && !selected.supportsEffortWithTools && selectedServers.size > 0;

  // Superseded by blockedReason/canSend below, which enforce the same conditions but also name
  // the one that is failing — a grey button with no explanation was the old behaviour.

  // "Copy as code": the current form state as a ready-to-run snippet.
  const [copyLanguage, setCopyLanguage] = useState<SnippetLanguage>('curl');
  const [copied, setCopied] = useState(false);
  const copySnippet = async () => {
    if (!selected) return;
    await navigator.clipboard.writeText(buildSnippet(copyLanguage, buildRequest(), apiBaseUrl));
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  // ---- Compare mode -------------------------------------------------------------------
  // Gated on the caller setting: a compare run bills every model separately. Columns are
  // independent requests and independent streams, so one failing leaves the others running —
  // there is deliberately no combined pre-flight cap.
  const compareAllowed = me.data?.compareModeEnabled ?? false;
  const [compareMode, setCompareMode] = useState(false);
  const [compareKeys, setCompareKeys] = useState<string[]>([]);
  const [compareRuns, setCompareRuns] = useState<Record<string, CompareRun>>({});
  const [comparing, setComparing] = useState(false);
  const compareAborts = useRef<AbortController[]>([]);

  const compareModels = compareKeys
    .map(k => (models.data ?? []).find(m => `${m.supplier}::${m.modelId}` === k))
    .filter((m): m is ModelInfo => !!m);

  /** One request per model, all in flight at once, each writing only into its own column. */
  const runCompare = () => {
    const prompt = message;
    setComparing(true);
    setCompareRuns(Object.fromEntries(compareKeys.map(k => [k, { text: '', tools: [] } as CompareRun])));
    compareAborts.current = [];

    const calls = compareModels.map(model => {
      const controller = new AbortController();
      compareAborts.current.push(controller);
      const key = `${model.supplier}::${model.modelId}`;
      const patch = (fn: (r: CompareRun) => CompareRun) =>
        setCompareRuns(prev => ({ ...prev, [key]: fn(prev[key] ?? { text: '', tools: [] }) }));

      const request: CompletionRequest = {
        ...buildRequest(),
        supplier: model.supplier,
        modelId: model.modelId,
        // Single-turn by design: keeping context across N models multiplies cost per turn and
        // the threads diverge until they are no longer comparable.
        messages: [{ role: 'User', content: prompt, attachments: attachments.length ? attachments : undefined }]
      };

      return api.completeStream(request, {
        onDelta: t => patch(r => ({ ...r, text: r.text + t })),
        onToolCall: c => patch(r => ({ ...r, tools: [...r.tools, c] })),
        onDone: res => patch(r => ({ ...r, response: res })),
        onError: (code, msg) => patch(r => ({ ...r, error: friendlyByCode[code] ?? msg }))
      }, controller.signal).catch((e: unknown) => {
        if (isAbortError(e)) patch(r => ({ ...r, error: 'Cancelled.' }));
        else patch(r => ({ ...r, error: friendlyError(e).message }));
      });
    });

    void Promise.allSettled(calls).then(() => setComparing(false));
  };

  const cancelCompare = () => compareAborts.current.forEach(c => c.abort());

  // Tags come from what each call actually reported — nothing estimated, nothing scored.
  const finishedRuns = Object.entries(compareRuns).filter(([, r]) => r.response);
  const cheapestKey = finishedRuns.length > 1
    ? finishedRuns.reduce((a, b) => (a[1].response!.usage.estimatedCostUsd <= b[1].response!.usage.estimatedCostUsd ? a : b))[0]
    : undefined;
  const fastestKey = finishedRuns.length > 1
    ? finishedRuns.reduce((a, b) => (a[1].response!.usage.latencyMs <= b[1].response!.usage.latencyMs ? a : b))[0]
    : undefined;

  /** Leaves compare with that exchange as turn one of an ordinary conversation. */
  const continueWith = (key: string) => {
    const chosen = compareRuns[key];
    if (!chosen?.response) return;
    setModelKey(key);
    setCompareMode(false);
    setTurns([
      { role: 'User', content: message, attachments: attachments.length ? attachments : undefined },
      {
        role: 'Assistant',
        content: chosen.response.content,
        usage: chosen.response.usage,
        finishReason: chosen.response.finishReason,
        correlationId: chosen.response.correlationId,
        toolCalls: chosen.response.toolCalls,
        toolBudgetExhausted: chosen.response.toolBudgetExhausted,
        servedFromGatewayCache: chosen.response.servedFromGatewayCache,
        piiRedactionApplied: chosen.response.piiRedactionApplied,
        supplier: chosen.response.supplier,
        modelId: chosen.response.modelId
      }
    ]);
    setMessage('');
    setAttachments([]);
    setCompareRuns({});
  };

  // Only settings that differ from default earn a chip — the point is that an unusual value
  // (the max-tokens 500 that truncated an answer) is impossible to miss.
  const chips: { label: string; tone?: 'warn' | 'on' }[] = [];
  if (maxTokens !== 10000) chips.push({ label: `max tokens ${fmtNumber(maxTokens)}`, tone: maxTokens < 2000 ? 'warn' : undefined });
  if (piiMode !== 'None') chips.push({ label: `PII · ${piiMode}` });
  if (effort) chips.push(effortDroppedForTools
    ? { label: `effort · ${effort} (dropped for tools)`, tone: 'warn' }
    : { label: `effort · ${effort}` });
  if (templateName) chips.push({ label: `skill · ${templateName}`, tone: 'on' });
  if (selectedServers.size > 0) chips.push({ label: `${selectedServers.size} tool server${selectedServers.size > 1 ? 's' : ''}`, tone: 'on' });
  if (selectedServers.size > 0 && maxToolCalls !== 20) chips.push({ label: `max ${maxToolCalls} tool calls` });
  if (systemPrompt.trim()) chips.push({ label: 'system prompt set' });
  if (!keepContext) chips.push({ label: 'context off', tone: 'warn' });

  // Why Send is blocked, said out loud rather than leaving a grey button to be puzzled over.
  const blockedReason = compareMode
    ? (compareModels.length < 2 ? 'Pick at least two models'
      : message.trim().length === 0 ? 'Type a message'
      : needsAcknowledgement && !acknowledgeUnscanned ? 'Acknowledge unscanned attachments'
      : toolsWithPii ? 'Tools need PII mode None'
      : missingCredentials.length > 0 ? `Enter your key for ${missingCredentials.map(s => s.name).join(', ')}`
      : null)
    : (!selected ? 'Pick a model first'
      : message.trim().length === 0 ? 'Type a message'
      : needsAcknowledgement && !acknowledgeUnscanned ? 'Acknowledge unscanned attachments'
      : toolsWithPii ? 'Tools need PII mode None'
      : missingCredentials.length > 0 ? `Enter your key for ${missingCredentials.map(s => s.name).join(', ')}`
      : null);

  const busy = run.isPending || comparing;

  // Follow the tail on every change that grows the transcript: completed turns, streamed deltas,
  // tool-call rows appearing, and each compare column filling in.
  useEffect(() => {
    if (!pinnedRef.current) return;
    const el = activeScroller();
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, streamText, liveTools, compareRuns, cancelled]);

  // A send is an explicit action, so it always jumps to the bottom even if the reader had
  // scrolled away — they just added the thing they want to watch.
  const pinToBottom = () => {
    pinnedRef.current = true;
    // Next frame, so the pending turn has rendered and scrollHeight includes it. Smooth is asked
    // for here rather than set in CSS, so it applies to this one deliberate jump and not to the
    // per-delta follow — and it honours a reduced-motion preference.
    requestAnimationFrame(() => {
      const el = activeScroller();
      if (!el) return;
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      el.scrollTo({ top: el.scrollHeight, behavior: reduced ? 'auto' : 'smooth' });
    });
  };
  const canSend = !blockedReason && !busy;

  const railBody = (
    <>
      {models.error && <div className="error-banner">Could not load models: {(models.error as Error).message}</div>}

      <Group title="Model &amp; skill" summary={`${selected?.modelId ?? 'none'}${templateName ? ` · ${templateName}` : ''}`} defaultOpen>
        <div className="field">
          <label htmlFor="model">Model</label>
          <select id="model" className="select" style={{ width: '100%' }} value={modelKey}
            onChange={e => setModelKey(e.target.value)} disabled={models.isLoading}>
            <option value="">{models.isLoading ? 'Loading models...' : 'Select a model...'}</option>
            {bySupplier.map(([supplier, list]) => (
              <optgroup key={supplier} label={supplier}>
                {list.map(m => (
                  <option key={m.modelId} value={`${m.supplier}::${m.modelId}`}>
                    {m.displayName} — {fmtUsd(m.inputCostPerMillionUsd)} in / {fmtUsd(m.outputCostPerMillionUsd)} out per 1M
                    {isTiered(m) ? ' (rises on long prompts)' : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {(hiddenDecisionsModels.data ?? 0) > 0 && (
            <div className="note-violet" style={{ marginTop: 8, fontSize: '.8rem', padding: '7px 9px', gap: 6 }}>
              <span className="pill pill-decisions pill-sm">Decisions</span>
              <span>
                {hiddenDecisionsModels.data} Decisions model{hiddenDecisionsModels.data === 1 ? ' isn’t' : 's aren’t'} listed here.{' '}
                <button type="button" className="link-btn" onClick={onOpenDecisions}>Open the Decisions tab</button>
              </span>
            </div>
          )}
          {selected && isTiered(selected) && (
            <div className="hint" style={{ borderLeft: '3px solid var(--kt-orange)', paddingLeft: 8, marginTop: 6 }}>
              <strong style={{ color: 'var(--kt-orange)' }}>Price rises with prompt size.</strong>
              <span style={{ display: 'block', marginTop: 2 }}>
                Up to {fmtNumber(selected.longContextThresholdTokens as number)} tokens:{' '}
                {fmtUsd(selected.inputCostPerMillionUsd)} in / {fmtUsd(selected.outputCostPerMillionUsd)} out per 1M
              </span>
              <span style={{ display: 'block' }}>
                Above that: {fmtUsd(selected.longContextInputCostPerMillionUsd as number)} in /{' '}
                {fmtUsd(selected.longContextOutputCostPerMillionUsd as number)} out per 1M — charged on the whole call.
              </span>
            </div>
          )}
          {selected && (
            <div className="hint">
              {selected.modelId} &middot; {fmtNumber(selected.maxContextTokens)} context tokens
              <span style={{ display: 'block', marginTop: 2 }}>
                {[
                  selected.supportsImageInput ? 'images' : null,
                  selected.supportsDocumentInput ? 'PDFs' : null,
                  selected.supportsFileTasks ? 'file tasks' : null,
                  selected.supportsEffort ? 'effort' : null
                ].filter(Boolean).join(' · ') || 'text only'}
              </span>
            </div>
          )}
        </div>
        <div className="field">
          <label htmlFor="template">Skill</label>
          <select id="template" className="select" style={{ width: '100%' }} value={templateName}
            onChange={e => {
              const chosen = templateOptions.find(t => t.name === e.target.value);
              setTemplateName(e.target.value);
              if (chosen?.suggestedSupplier && chosen.suggestedModelId) {
                const key = `${chosen.suggestedSupplier}::${chosen.suggestedModelId}`;
                if ((models.data ?? []).some(m => `${m.supplier}::${m.modelId}` === key)) setModelKey(key);
              }
              if (chosen?.suggestedEffort) setEffort(chosen.suggestedEffort);
              if (chosen?.suggestedMaxTokens) setMaxTokens(chosen.suggestedMaxTokens);
            }}>
            <option value="">None</option>
            {templateOptions.map(t => <option key={t.id} value={t.name} title={t.description ?? undefined}>{t.name}</option>)}
          </select>
          {selectedTemplate?.description && <div className="hint">{selectedTemplate.description}</div>}
          {selectedTemplate && selectedTemplate.files.length > 0 && (
            <div className="hint">
              Includes {selectedTemplate.files.length} reference document{selectedTemplate.files.length > 1 ? 's' : ''}
            </div>
          )}
        </div>
      </Group>

      <Group title="System prompt" summary={systemPrompt.trim() ? 'set' : 'none'}>
        <div className="field">
          <label htmlFor="system">Optional{templateName ? ' — appended after the skill' : ''}</label>
          <textarea id="system" rows={4} placeholder="e.g. You are a concise assistant for Key Travel staff."
            value={systemPrompt} onChange={e => setSystemPrompt(e.target.value)} />
        </div>
      </Group>

      {(mcpServers.data ?? []).length > 0 && (
        <Group title="Tools" summary={`${selectedServers.size} of ${(mcpServers.data ?? []).length}`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {(mcpServers.data ?? []).map(s => (
              <label key={s.name} title={s.description ?? undefined} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '.84rem' }}>
                <input type="checkbox" style={{ width: 'auto' }} checked={selectedServers.has(s.name)}
                  onChange={() => setSelectedServers(prev => {
                    const next = new Set(prev);
                    if (next.has(s.name)) next.delete(s.name); else next.add(s.name);
                    return next;
                  })} />
                <strong>{s.name}</strong>
                <span style={{ color: 'var(--kt-grey-400)', fontSize: '.76rem' }}>({s.tools.length})</span>
                {s.requiresUserCredential && (
                  <span className="pill pill-info" title="Runs as you — you supply your own credential.">your key</span>
                )}
              </label>
            ))}
          </div>
          {credentialServers.length > 0 && (
            <div style={{ marginTop: 10, display: 'grid', gap: 12 }}>
              {credentialServers.map(s => (
                <McpCredentialField key={s.name} server={s} value={serverCredentials[s.name] ?? ''}
                  onChange={value => setServerCredentials(prev => ({ ...prev, [s.name]: value }))}
                  onSaved={() => mcpServers.refetch()} />
              ))}
            </div>
          )}
          {selectedServers.size > 0 && (
            <div className="field" style={{ marginTop: 10 }}>
              <label htmlFor="maxToolCalls">Max tool calls</label>
              <input id="maxToolCalls" type="number" min={1} max={toolCallCeiling} value={maxToolCalls}
                onChange={e => setMaxToolCalls(Math.max(1, Math.min(toolCallCeiling, Number(e.target.value) || 1)))} />
              <div className="hint">
                How many tools the model may call before it must answer. Your limit is {toolCallCeiling} —
                an administrator can raise it. Discover-then-query tasks need headroom.
              </div>
            </div>
          )}
          {selectedServers.size > 0 && (
            <div className="hint">The loop runs on the gateway and every call is metered. Expect longer latencies.</div>
          )}
          {toolsWithPii && (
            <div style={{ color: 'var(--kt-red)', fontSize: '.8rem', marginTop: 4 }}>
              Tools require PII mode None — redaction cannot cover tool traffic.
            </div>
          )}
          {missingCredentials.length > 0 && (
            <div style={{ color: 'var(--kt-red)', fontSize: '.8rem', marginTop: 4 }}>
              Enter your key for {missingCredentials.map(s => s.name).join(', ')} to run — save it and you will not be asked again.
            </div>
          )}
        </Group>
      )}

      <Group title="Limits &amp; safety" summary={`${fmtNumber(maxTokens)} · ${piiMode}${effort ? ` · ${effort}` : ''}`}>
        <div className="field">
          <label htmlFor="maxTokens">Max tokens</label>
          <input id="maxTokens" type="number" min={1} step={1} value={maxTokens}
            onChange={e => setMaxTokens(Number(e.target.value) || 10000)} />
        </div>
        <div className="field">
          <label htmlFor="pii">PII mode</label>
          <select id="pii" className="select" style={{ width: '100%' }} value={piiMode}
            onChange={e => setPiiMode(e.target.value as PiiMode)}>
            {piiModes.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="effort">Reasoning effort</label>
          <select id="effort" className="select" style={{ width: '100%' }} value={effort}
            disabled={!!selected && !selected.supportsEffort}
            onChange={e => setEffort(e.target.value as AiEffort | '')}>
            <option value="">Supplier default</option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="XHigh">XHigh</option>
            <option value="Max">Max</option>
          </select>
          <div className="hint">
            {selected && !selected.supportsEffort
              ? 'Not supported by this model'
              : effortDroppedForTools
                ? 'Dropped while tools are on — this model won’t reason and call tools on the same turn. It reasons again for the final answer.'
                : 'Only for models that list effort under the model picker'}
          </div>
        </div>
      </Group>

      <Group title="Attachments" summary={attachments.length ? `${attachments.length} file${attachments.length > 1 ? 's' : ''}` : 'none'}>
        <input ref={fileInputRef} type="file" multiple
          accept={support.documents ? 'image/jpeg,image/png,image/gif,image/webp,application/pdf' : 'image/jpeg,image/png,image/gif,image/webp'}
          style={{ display: 'none' }} onChange={e => addFiles(e.target.files)} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="btn-secondary btn-sm" disabled={!selected || !support.images}
            onClick={() => fileInputRef.current?.click()}>
            Attach {support.documents ? 'image / PDF' : 'image'}
          </button>
          {selected && !support.images && (
            <span style={{ fontSize: '.78rem', color: 'var(--kt-grey-400)' }}>
              {selected.supplier} does not accept attachments
            </span>
          )}
          {attachments.map((a, i) => (
            <span key={`${a.fileName}-${i}`} className="pill pill-disabled" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              {a.kind === 'Image' ? 'Image' : 'PDF'} · {a.fileName ?? a.mediaType}
              <button type="button" aria-label={`Remove ${a.fileName}`}
                onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}>✕</button>
            </span>
          ))}
        </div>
        {attachError && <div style={{ color: 'var(--kt-red)', fontSize: '.8rem', marginTop: 6 }}>{attachError}</div>}
        {needsAcknowledgement && (
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8, fontSize: '.82rem', color: 'var(--kt-grey-700)' }}>
            <input type="checkbox" checked={acknowledgeUnscanned} style={{ width: 'auto', marginTop: 2 }}
              onChange={e => setAcknowledgeUnscanned(e.target.checked)} />
            <span>I understand PII redaction only covers text — attached files are sent to the supplier unscanned.</span>
          </label>
        )}
      </Group>
    </>
  );

  return (
    <>
      <div className="pg-sub">
            {compareMode
              ? 'One prompt across several models. Everything but the model is held constant, and each model is billed separately.'
              : keepContext
                ? 'Hold a conversation with a model — each turn resends the thread, and every call is billed against your monthly budget.'
                : 'Try a model with a one-off prompt. Calls are billed against your monthly budget.'}
      </div>

      <div className="wb">
        <div className="wb-strip">
          <button type="button" className="btn-secondary btn-sm wb-sheet-btn" onClick={() => setRailOpen(o => !o)}>
            {railOpen ? 'Hide settings' : 'Settings'}
          </button>

          {compareAllowed && (
            <div style={{ display: 'flex', gap: 4 }}>
              <button type="button" className={compareMode ? 'btn-ghost btn-sm' : 'btn-secondary btn-sm'}
                onClick={() => setCompareMode(false)} disabled={busy}>Single</button>
              <button type="button" className={compareMode ? 'btn-secondary btn-sm' : 'btn-ghost btn-sm'}
                onClick={() => setCompareMode(true)} disabled={busy}>Compare</button>
            </div>
          )}

          {compareMode ? (
            <div className="wb-chips">
              {compareModels.map(m => (
                <span key={m.modelId} className="wb-chip on">
                  {m.modelId}
                  <button type="button" aria-label={`Remove ${m.modelId}`} disabled={busy}
                    onClick={() => setCompareKeys(k => k.filter(x => x !== `${m.supplier}::${m.modelId}`))}
                    style={{ border: 0, background: 'none', cursor: 'pointer', marginLeft: 5, color: 'inherit' }}>✕</button>
                </span>
              ))}
              {compareKeys.length < 4 && (
                <select className="select" style={{ padding: '3px 8px', fontSize: '.78rem' }} value=""
                  disabled={busy}
                  onChange={e => { if (e.target.value) setCompareKeys(k => [...k, e.target.value]); }}>
                  <option value="">+ Add model</option>
                  {(models.data ?? [])
                    .filter(m => !compareKeys.includes(`${m.supplier}::${m.modelId}`))
                    .map(m => <option key={`${m.supplier}::${m.modelId}`} value={`${m.supplier}::${m.modelId}`}>{m.modelId}</option>)}
                </select>
              )}
            </div>
          ) : (
            <>
              <span className="wb-chip on">{selected?.modelId ?? 'no model'}</span>
              {turns.length > 0 && <span className="wb-chip mute">{turns.length} message{turns.length > 1 ? 's' : ''}</span>}
            </>
          )}

          {sessionCostUsd > 0 && <span className="wb-chip mute" title="Billed across this conversation">{fmtUsd(sessionCostUsd)}</span>}

          {!compareMode && contextUsed !== undefined && contextLimit
            ? <ContextMeter used={contextUsed} limit={contextLimit} turns={turns.length} keepContext={keepContext}
                thresholdTokens={selected?.longContextThresholdTokens ?? null} />
            : <span style={{ flex: 1 }} />}

          {busy && (
            <button className="btn-danger btn-sm" onClick={compareMode ? cancelCompare : cancelRun}>Cancel</button>
          )}
          {!compareMode && turns.length > 0 && (
            <button className="btn-ghost btn-sm" onClick={newConversation} disabled={busy}>New conversation</button>
          )}
        </div>

        <div className="wb-body">
          <aside className={railOpen ? 'wb-rail open' : 'wb-rail'}>{railBody}</aside>

          <div className="wb-main">
            {chips.length > 0 && (
              <div className="wb-chips">
                {chips.map((c, i) => (
                  <span key={i} className={c.tone ? `wb-chip ${c.tone}` : 'wb-chip'}>{c.label}</span>
                ))}
                <span className="wb-chip mute">defaults hidden</span>
              </div>
            )}

            <div className="wb-scroll" ref={scrollRef} onScroll={onTranscriptScroll}>
              {compareMode ? (
                compareKeys.length === 0 ? (
                  <div className="placeholder">Add two or more models, then ask them all the same thing.</div>
                ) : (
                  <div className="wb-cols">
                    {compareModels.map(m => {
                      const key = `${m.supplier}::${m.modelId}`;
                      const r = compareRuns[key];
                      return (
                        <div className="wb-col" key={key}>
                          <div className="wb-col-head">
                            <span className="wb-col-name" title={m.modelId}>{m.modelId}</span>
                            <span className="wb-chips">
                              {fastestKey === key && <span className="wb-chip on">fastest</span>}
                              {cheapestKey === key && <span className="wb-chip on">cheapest</span>}
                            </span>
                          </div>
                          <div className="wb-col-body">
                            {r && r.tools.length > 0 && <ToolCalls calls={r.tools} running={comparing && !r.response} />}
                            {r?.error
                              ? <div className="error-banner" style={{ marginBottom: 0 }}>{r.error}</div>
                              : r?.text
                                ? <MarkdownAnswer text={r.text} streaming={comparing && !r.response} />
                                : <span style={{ color: 'var(--kt-grey-400)' }}>{comparing ? 'Waiting...' : 'Not run yet'}</span>}
                          </div>
                          {r?.response && (
                            <div className="wb-col-foot">
                              <div className="wb-chips">
                                <span className="wb-chip mute">out {fmtNumber(r.response.usage.outputTokens)}</span>
                                <span className="wb-chip mute">{fmtUsd(r.response.usage.estimatedCostUsd)}</span>
                                <span className="wb-chip mute">{fmtNumber(r.response.usage.latencyMs)} ms</span>
                              </div>
                              <button className="btn-secondary btn-sm" onClick={() => continueWith(key)}>
                                Continue with this
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (
                <>
                  {run.isError && !isAbortError(run.error) && <RunError error={run.error} />}

                  {cancelled !== null && !run.isPending && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{
                        padding: '8px 12px', marginBottom: cancelled ? 8 : 0, fontSize: '.8rem',
                        color: 'var(--kt-orange)', background: 'var(--kt-orange-bg)',
                        border: '1px solid var(--kt-orange-border)', borderRadius: 'var(--radius-sm)'
                      }}>
                        Cancelled. {cancelled ? 'The partial answer below is kept out of the conversation, so it will not be sent as context.' : 'Nothing had arrived yet.'}
                        {' '}Tokens generated before you stopped are still billed — see Usage.
                      </div>
                      {cancelled && <div className="completion-output bounded" style={{ opacity: .75 }}>{cancelled}</div>}
                    </div>
                  )}

                  {turns.length === 0 && cancelled === null && !run.isError && !run.isPending && (
                    <div className="placeholder">
                      {keepContext ? 'Start a conversation — replies stay in context.' : 'Run a prompt to see the response here.'}
                    </div>
                  )}

                  {turns.map((turn, i) => {
                    const turnModel = (models.data ?? []).find(
                      m => m.supplier === turn.supplier && m.modelId === turn.modelId);
                    const billedLong = !!turn.usage && !!turn.supplier
                      && isLongContext(turnModel, promptTokensOf(turn.supplier, turn.usage));
                    return <TurnBlock key={i} turn={turn} longContextBilled={billedLong} />;
                  })}

                  {run.isPending && (
                    <div style={{ marginTop: turns.length > 0 ? 16 : 0 }}>
                      <div className="turn-role">You</div>
                      <div className="completion-output turn-user">{inFlight?.content ?? message}</div>
                      <div style={{ margin: liveTools.length > 0 ? '10px 0' : 0 }}>
                        <ToolCalls calls={liveTools} running />
                      </div>
                      <div className="turn-role" style={{ marginTop: 10 }}>Model</div>
                      {streamText ? <AnswerBody text={streamText} streaming /> : <div className="placeholder">Waiting for the model...</div>}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="wb-composer">
              <textarea
                id="message"
                rows={4}
                aria-label={turns.length > 0 && keepContext && !compareMode ? 'Your reply' : 'Your message'}
                placeholder={compareMode
                  ? 'Ask every selected model the same thing...'
                  : turns.length > 0 && keepContext ? 'Continue the conversation...' : 'Ask the model something...'}
                value={message}
                onChange={e => setMessage(e.target.value)}
                onKeyDown={e => {
                  // Enter sends, Shift+Enter newlines. Guarded on the same checks as the button.
                  if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                    e.preventDefault();
                    if (canSend) { pinToBottom(); if (compareMode) runCompare(); else run.mutate(); }
                  }
                }}
              />
              <div className="wb-composer-row">
                {!compareMode && (
                  <label className="wb-chip" title="Each turn resends the whole thread, so the model remembers — and every turn costs more than the last."
                    style={{ display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                    <input type="checkbox" checked={keepContext} disabled={busy} style={{ width: 'auto' }}
                      onChange={e => changeKeepContext(e.target.checked)} />
                    keep context
                  </label>
                )}
                {compareMode && <span className="wb-chip mute">context off in compare · each model billed separately</span>}

                <span style={{ flex: 1 }} />

                {blockedReason && <span className="wb-chip warn">{blockedReason}</span>}
                <span className="wb-chip mute">⏎ sends</span>

                <select value={copyLanguage} onChange={e => setCopyLanguage(e.target.value as SnippetLanguage)}
                  aria-label="Snippet language" className="select" style={{ padding: '5px 7px', fontSize: '.78rem' }}>
                  {snippetLanguages.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
                <button className="btn-secondary btn-sm" disabled={!selected} onClick={copySnippet}
                  title={selected ? 'Copy this request as code' : 'Pick a model first'}>
                  {copied ? 'Copied ✓' : 'Copy as code'}
                </button>

                <button className="btn-primary" disabled={!canSend}
                  onClick={() => { pinToBottom(); if (compareMode) runCompare(); else run.mutate(); }}>
                  {busy ? 'Running...' : compareMode ? `Run on ${compareModels.length} model${compareModels.length === 1 ? '' : 's'}` : turns.length > 0 && keepContext ? 'Send' : 'Run'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/** One model's column in a compare run: its own stream, its own failure. */
interface CompareRun {
  text: string;
  tools: StreamToolCall[];
  response?: CompletionResponse;
  error?: string;
}

/**
 * A collapsible config section that states what it holds while closed, so the rail stays short
 * without hiding what is set.
 */
function Group({ title, summary, children, defaultOpen }: {
  title: string; summary: string; children: React.ReactNode; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <section className={open ? 'wb-grp open' : 'wb-grp'}>
      <button type="button" className="wb-grp-head" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <span>{title}</span>
        {!open && <span className="wb-grp-sum">{summary}</span>}
      </button>
      {open && <div className="wb-grp-body">{children}</div>}
    </section>
  );
}

/**
 * A model answer, rendered as markdown with the source one click away.
 *
 * The raw toggle is not decoration: this is a test harness, and when you're checking whether a
 * skill emits valid JSON or well-formed markdown, the literal output IS the thing under test.
 * Render by default, but never take the source away.
 */
function AnswerBody({ text, streaming }: { text: string; streaming?: boolean }) {
  const [raw, setRaw] = useState(false);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
        <button
          type="button"
          className="btn-ghost btn-sm"
          style={{ fontSize: '.72rem', padding: '2px 8px' }}
          onClick={() => setRaw(r => !r)}>
          {raw ? 'Rendered' : 'Raw'}
        </button>
      </div>
      {raw
        ? <div className="completion-output">{text}</div>
        : <MarkdownAnswer text={text} streaming={streaming} />}
    </div>
  );
}


/**
 * A finish reason as a sentence, not a raw enum value. "Length" told nobody anything: it means
 * the answer was cut off at the output cap, and the fix is a setting the reader can act on.
 * Returns null for ordinary completions, which need no commentary.
 */
function finishNote(turn: Turn): string | null {
  if (turn.toolBudgetExhausted) {
    return 'Stopped at the tool-call limit — the model ran out of tool calls before finishing, so this answer may be incomplete. Raise Max tool calls and re-run.';
  }
  const reason = (turn.finishReason ?? '').toLowerCase();
  if (reason === 'length' || reason === 'maxtokens' || reason === 'max_tokens') {
    return 'Stopped at the output limit rather than finishing — raise Max tokens and re-run to get the rest.';
  }
  if (reason === 'contentfilter' || reason === 'content_filter') {
    return 'The supplier stopped this answer on its own content filter.';
  }
  return null;
}

/** Shape common to a finished ToolCallSummary and an in-flight StreamToolCall. */
interface ToolCallLine {
  server: string;
  tool: string;
  durationMs?: number;
  success?: boolean;
  state?: 'started' | 'completed';
}

/**
 * The caller's own key for one MCP server that runs tools as them.
 *
 * Saved keys live in Key Vault and are never read back to the browser, so a saved key shows as a
 * hint and nothing more. Typing a key here always sends it on the next request and takes
 * precedence over the saved one for that request only — the escape hatch for running as a
 * different identity without disturbing what's stored.
 */
function McpCredentialField({ server, value, onChange, onSaved }: {
  server: AvailableMcpServer;
  value: string;
  onChange: (value: string) => void;
  onSaved: () => void;
}) {
  const api = useApi();
  const [overriding, setOverriding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typed = value.trim();
  const showInput = !server.hasSavedCredential || overriding;

  const save = useMutation({
    mutationFn: () => api.saveMyMcpCredential(server.name, typed),
    onSuccess: () => {
      // Cleared once stored: the saved key covers every later message, so leaving it in the box
      // would send it as an override on requests that no longer need one.
      onChange('');
      setOverriding(false);
      setError(null);
      onSaved();
    },
    onError: e => setError(friendlyError(e).message)
  });

  const forget = useMutation({
    mutationFn: () => api.forgetMyMcpCredential(server.name),
    onSuccess: () => { setError(null); onSaved(); },
    onError: e => setError(friendlyError(e).message)
  });

  const busy = save.isPending || forget.isPending;

  return (
    <div className="field" style={{ margin: 0 }}>
      <label htmlFor={`cred-${server.name}`}>Your key for <strong>{server.name}</strong></label>

      {server.hasSavedCredential && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: showInput ? 8 : 0 }}>
          <span className="pill pill-success">
            saved{server.savedCredentialHint ? ` ····${server.savedCredentialHint}` : ''}
          </span>
          {!overriding && (
            <button type="button" className="btn-ghost btn-sm" onClick={() => setOverriding(true)}>
              Use a different key once
            </button>
          )}
          <button type="button" className="btn-ghost btn-sm" disabled={busy} onClick={() => forget.mutate()}>
            Forget
          </button>
        </div>
      )}

      {showInput && (
        <>
          <input
            id={`cred-${server.name}`}
            type="password"
            autoComplete="off"
            placeholder="Your subscription key / token"
            value={value}
            onChange={e => onChange(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={!typed || busy}
              onClick={() => save.mutate()}>
              {server.hasSavedCredential ? 'Replace my saved key' : 'Save this key'}
            </button>
            {overriding && (
              <button
                type="button"
                className="btn-ghost btn-sm"
                disabled={busy}
                onClick={() => { setOverriding(false); onChange(''); setError(null); }}>
                Cancel
              </button>
            )}
          </div>
        </>
      )}

      <div className="hint">
        {typed
          ? `Used for this message only — ${server.hasSavedCredential ? 'your saved key is untouched' : 'nothing is stored'} unless you save it.`
          : `Sent to ${server.name} as your identity so it honours your own permissions. Saved keys are held in Key Vault and never shown again.`}
      </div>

      {error && <div style={{ color: 'var(--kt-red)', fontSize: '.8rem', marginTop: 4 }}>{error}</div>}
    </div>
  );
}

/**
 * Tool calls behind a count. A 20-call loop otherwise buries the answer it belongs to, and in a
 * long thread that repeats for every turn — so finished calls collapse, and the summary carries
 * the part worth seeing at a glance (how many, and whether any failed).
 *
 * A run in flight stays open: while streaming this list is the only evidence anything is
 * happening, and collapsing it by default would trade a real progress signal for tidiness.
 */
function ToolCalls({ calls, running }: { calls: ToolCallLine[]; running?: boolean }) {
  if (calls.length === 0) return null;

  const failed = calls.filter(c => c.state !== 'started' && c.success === false).length;

  return (
    <details className="tool-calls" open={running}>
      <summary>
        {calls.length} tool call{calls.length === 1 ? '' : 's'}
        {failed > 0 && <span style={{ color: 'var(--kt-red)' }}>&nbsp;· {failed} failed</span>}
        {running && <span style={{ color: 'var(--kt-grey-400)', fontWeight: 400 }}>&nbsp;· running...</span>}
      </summary>
      <div className="tool-calls-body">
        {calls.map((t, i) => (
          <div key={i} style={{ color: t.state === 'started' ? 'var(--kt-grey-500)' : t.success ? 'var(--kt-grey-700)' : 'var(--kt-red)' }}>
            {t.state === 'started' ? '⏳' : t.success ? '✓' : '✗'} {t.server} / {t.tool}
            {t.state !== 'started' && t.durationMs !== undefined && <> · {fmtNumber(t.durationMs)} ms</>}
          </div>
        ))}
      </div>
    </details>
  );
}

/** One transcript entry: the text, plus the usage strip for model turns. */
function TurnBlock({ turn, longContextBilled }: { turn: Turn; longContextBilled?: boolean }) {
  const isUser = turn.role === 'User';
  const note = finishNote(turn);
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="turn-role">{isUser ? 'You' : 'Model'}</div>
      {isUser && turn.attachments && turn.attachments.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          {turn.attachments.map((a, i) => (
            <span key={i} className="pill pill-disabled">
              {a.kind === 'Image' ? 'Image' : 'PDF'} · {a.fileName ?? a.mediaType}
            </span>
          ))}
        </div>
      )}
      {!isUser && turn.toolCalls && <ToolCalls calls={turn.toolCalls} />}

      {isUser
        ? <div className="completion-output turn-user">{turn.content}</div>
        : <AnswerBody text={turn.content} />}
      {note && (
        <div style={{
          marginTop: 8, padding: '8px 12px', fontSize: '.8rem',
          color: 'var(--kt-orange)', background: 'var(--kt-orange-bg)',
          border: '1px solid var(--kt-orange-border)', borderRadius: 'var(--radius-sm)'
        }}>
          {note}
        </div>
      )}
      {turn.usage && (
        <div className="usage-strip">
          <Metric label="Tokens in" value={fmtNumber(turn.usage.inputTokens)} />
          <Metric label="Tokens out" value={fmtNumber(turn.usage.outputTokens)} />
          <Metric label="Cache read" value={fmtNumber(turn.usage.cacheReadInputTokens)} />
          <Metric label="Cost" value={fmtUsd(turn.usage.estimatedCostUsd)} />
          <Metric label="Latency" value={`${fmtNumber(turn.usage.latencyMs)} ms`} />
          {turn.modelId && <Metric label="Model" value={turn.modelId} />}
          {longContextBilled && (
            <span className="wb-chip warn" style={{ alignSelf: 'center' }}
              title="This prompt was over the model's long-context threshold, so the whole call billed at the higher rate.">
              long-context rate
            </span>
          )}
          {turn.finishReason && <Metric label="Finish" value={turn.finishReason} />}
          {turn.piiRedactionApplied && <Metric label="PII" value="redacted" />}
          {turn.servedFromGatewayCache && (
            <span className="pill pill-success" style={{ alignSelf: 'center' }}>served from gateway cache</span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * How much of the model's context window the thread is occupying. Measured from the last
 * completed turn rather than estimated from the draft — only the supplier can say what the
 * prompt actually tokenised to, and a wrong estimate here would be worse than none.
 */
function ContextMeter({ used, limit, turns, keepContext, thresholdTokens }: {
  used?: number; limit?: number; turns: number; keepContext: boolean; thresholdTokens?: number | null;
}) {
  if (used === undefined || !limit) return null;

  // On a tiered model the meter carries the more useful warning: crossing the threshold changes
  // the price per token, and that happens long before the window itself runs out.
  const overThreshold = !!thresholdTokens && used > thresholdTokens;

  const pct = Math.min(100, (used / limit) * 100);
  // Thresholds are about headroom for the NEXT turn, not this one: at 75% a long reply can
  // still land, past 90% it very likely cannot.
  const colour = pct >= 90 ? 'var(--kt-red)' : pct >= 75 ? 'var(--kt-orange)' : 'var(--kt-green)';

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: '.78rem', marginBottom: 5 }}>
        <span style={{ fontWeight: 600, color: 'var(--kt-grey-700)' }}>
          Context {keepContext ? `· ${turns} message${turns === 1 ? '' : 's'}` : '· last request'}
        </span>
        <span style={{ color: 'var(--kt-grey-500)', fontVariantNumeric: 'tabular-nums' }}>
          {fmtNumber(used)} / {fmtNumber(limit)} tokens ({pct < 1 ? '<1' : Math.round(pct)}%)
          {thresholdTokens ? (
            <span
              className={overThreshold ? 'wb-chip warn' : 'wb-chip'}
              style={{ marginLeft: 8 }}
              title={overThreshold
                ? `Past ${fmtNumber(thresholdTokens)} tokens, so this model is billing at its higher long-context rates.`
                : `Stays at the lower rate until ${fmtNumber(thresholdTokens)} tokens.`}>
              {overThreshold ? 'long-context rate' : 'standard rate'}
            </span>
          ) : null}
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: 'var(--kt-grey-200)', overflow: 'hidden' }}>
        <div style={{ width: `${Math.max(pct, 1)}%`, height: '100%', background: colour, transition: 'width .3s' }} />
      </div>
      {keepContext && pct >= 75 && (
        <div style={{ fontSize: '.76rem', color: pct >= 90 ? 'var(--kt-red)' : 'var(--kt-orange)', marginTop: 5 }}>
          {pct >= 90
            ? 'Nearly full — the next turn may be rejected for exceeding the window. Start a new conversation, or turn off "Keep context".'
            : 'Filling up — each turn resends the whole thread, so both cost and context grow as you go.'}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <span className="metric">
      <span className="metric-label">{label}</span>
      <span className="metric-value">{value}</span>
    </span>
  );
}

function RunError({ error }: { error: unknown }) {
  const { message, code } = friendlyError(error);
  return (
    <div className="error-banner">
      {message}
      {code && <div style={{ fontSize: '.78rem', marginTop: 4, fontFamily: 'ui-monospace, Consolas, monospace', opacity: .8 }}>code: {code}</div>}
    </div>
  );
}
